import React, { useEffect, useState } from "react";
import { Plus, UtensilsCrossed, FileDown, Pencil, Trash2, Copy, History, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import MenuEditor from "@/components/menus/MenuEditor";
import MenuEmailDialog from "@/components/menus/MenuEmailDialog";
import { Mail } from "lucide-react";
import SearchBox, { matches } from "@/components/SearchBox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
const dLabel = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "long" });

export default function Menus() {
  const [menus, setMenus] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [emailMenu, setEmailMenu] = useState(null);
  const [copyMenu, setCopyMenu] = useState(null);
  const [search, setSearch] = useState("");
  const [diffs, setDiffs] = useState([]);
  const [refName, setRefName] = useState("");

  const load = async () => {
    const [m, c, d] = await Promise.all([api.get("/menus"), api.get("/customers"), api.get("/menus/differences", { params: { month: thisMonth() } })]);
    setMenus(m.data); setCustomers(c.data); setDiffs(d.data.differences || []); setRefName(d.data.reference?.name || "");
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Bu menü silinsin mi?")) return;
    try { await api.delete(`/menus/${id}`); toast.success("Menü silindi"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copy = async (m, target) => {
    try { const { data } = await api.post(`/menus/${m.id}/copy`, null, { params: { month: target } }); toast.success("Menü kopyalandı"); setCopyMenu(null); await load(); setEditing(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cur = thisMonth();
  const menuMatch = (m) => matches(search, m.name, m.month, monthLabel(m.month), m.note, ...(m.customers || []).flatMap((c) => [c.name, c.short_name]), ...Object.values(m.days || {}).flatMap((d) => (Array.isArray(d) ? d : Object.values(d || {}))));
  const shown = menus.filter(menuMatch);
  const current = shown.filter((m) => m.month >= cur);
  const past = shown.filter((m) => m.month < cur);

  if (editing) return <MenuEditor menu={editing} customers={customers} onClose={() => { setEditing(null); load(); }} />;

  return (
    <div className="space-y-6" data-testid="menus-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Aylık Menüler</h1>
          <p className="text-sm text-muted-foreground mt-1">Her ay için öğle yemeği menüsü yazın, müşterilere atayın, PDF görüntüleyin veya e-posta ile gönderin.</p>
        </div>
        <Button onClick={() => setEditing({ month: cur, name: `${monthLabel(cur)} Menüsü`, days: {}, customer_ids: [], note: "" })} data-testid="menu-add-btn" className="rounded-full h-11 px-6 font-semibold gap-2"><Plus className="h-4 w-4" /> Yeni Menü</Button>
      </div>

      <SearchBox value={search} onChange={setSearch} placeholder="Menü adı, yemek, müşteri ara…" testId="menus-search-input" className="w-full sm:w-[320px]" />

      {diffs.length > 0 && (
        <Card className="p-4 border-amber-500/40 bg-amber-500/5" data-testid="menu-diff-summary">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 text-amber-600 font-heading font-semibold"><AlertTriangle className="h-4 w-4" /> Referans Menüden Farklı Yemek Çıkan Günler</div>
            <a href={`${BACKEND}/api/menus/differences/pdf?month=${thisMonth()}`} target="_blank" rel="noreferrer" data-testid="menu-diff-pdf"><Button variant="outline" size="sm" className="rounded-full gap-1.5 h-8"><FileDown className="h-3.5 w-3.5" /> PDF</Button></a>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">Ayın ilk yazılan menüsü <b>referans</b> alınır; diğer atanan menülerde yapılan değişiklikler atanan müşterisiyle burada listelenir. Ay bitince önceki ayın farklılıkları kaybolur.</p>
          {refName && <p className="text-xs text-muted-foreground mt-1">Referans menü: <span className="font-semibold text-foreground">{refName}</span></p>}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
            {diffs.map((d) => (
              <div key={d.date} className="rounded-lg border border-border bg-background p-2.5" data-testid={`menu-diff-${d.date}`}>
                <div className="text-sm font-semibold font-mono">{dLabel(d.date)}</div>
                {d.ref_meal && <div className="text-[11px] text-muted-foreground mt-0.5">Referans: {d.ref_meal}</div>}
                <div className="mt-1 space-y-1">
                  {d.items.map((g, i) => (
                    <div key={i} className="text-xs"><span className="font-medium">{g.customers.join(", ") || "Müşteri atanmadı"}</span>: {(g.dishes || [{ text: g.meal, diff: true }]).map((x, j) => (
                      <span key={j}>{j > 0 ? ", " : ""}<span className={x.diff ? "font-semibold text-red-600 dark:text-red-400" : ""}>{x.text}</span></span>
                    ))}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Section title="Güncel & Gelecek Aylar" items={current} customers={customers} onEdit={setEditing} onDelete={del} onCopy={setCopyMenu} onEmail={setEmailMenu} emptyText="Henüz menü yok — Yeni Menü ile başlayın" testId="menus-current" />

      <div>
        <button onClick={() => setShowPast(!showPast)} data-testid="menus-toggle-past" className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground">
          <History className="h-4 w-4" /> Geçmiş Menüler ({past.length}) {showPast ? "▲" : "▼"}
        </button>
        {showPast && <div className="mt-3"><Section items={past} customers={customers} onEdit={setEditing} onDelete={del} onCopy={setCopyMenu} onEmail={setEmailMenu} emptyText="Geçmiş menü yok" testId="menus-past" /></div>}
      </div>
      {emailMenu && <MenuEmailDialog menu={emailMenu} onClose={() => setEmailMenu(null)} onSent={load} />}
      {copyMenu && <CopyMenuDialog menu={copyMenu} onClose={() => setCopyMenu(null)} onCopy={copy} />}
    </div>
  );
}

const CopyMenuDialog = ({ menu, onClose, onCopy }) => {
  const [target, setTarget] = useState(nextMonth(menu.month));
  const [busy, setBusy] = useState(false);
  const valid = /^\d{4}-\d{2}$/.test(target);
  const submit = async () => {
    if (!valid) { toast.error("Geçerli bir ay seçin"); return; }
    setBusy(true);
    await onCopy(menu, target);
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="menu-copy-dialog">
        <DialogHeader><DialogTitle>Menüyü Kopyala</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">{menu.name}</span> menüsü, tüm günleri ve atanan müşterileriyle seçtiğiniz aya kopyalanır.</p>
          <div>
            <Label htmlFor="copy-month">Hedef ay</Label>
            <Input id="copy-month" type="month" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="menu-copy-month-input" className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="menu-copy-cancel">İptal</Button>
          <Button onClick={submit} disabled={busy || !valid} className="rounded-full" data-testid="menu-copy-confirm">Kopyala</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const nextMonth = (m) => { const [y, mo] = m.split("-").map(Number); const d = new Date(y, mo, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };

const Section = ({ title, items, onEdit, onDelete, onCopy, onEmail, emptyText, testId }) => (
  <div className="space-y-3" data-testid={testId}>
    {title && <h3 className="font-heading text-lg font-semibold">{title}</h3>}
    {items.length === 0 && <Card className="p-8 text-center text-muted-foreground" data-testid={`${testId}-empty`}>{emptyText}</Card>}
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {items.map((m) => (
        <Card key={m.id} className="p-4" data-testid={`menu-card-${m.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><UtensilsCrossed className="h-5 w-5" /></div>
              <div className="min-w-0">
                <div className="font-heading font-semibold truncate">{m.name}</div>
                <div className="text-xs text-muted-foreground">{monthLabel(m.month)} · {m.filled_days} gün dolu</div>
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <a href={`${BACKEND}/api/menus/${m.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`menu-pdf-${m.id}`} title="PDF" className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-foreground"><FileDown className="h-4 w-4" /></a>
              <button onClick={() => onEmail(m)} data-testid={`menu-email-${m.id}`} title="E-posta ile gönder" className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-foreground"><Mail className="h-4 w-4" /></button>
              <button onClick={() => onCopy(m)} data-testid={`menu-copy-${m.id}`} title="Başka aya kopyala" className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-foreground"><Copy className="h-4 w-4" /></button>
              <button onClick={() => onEdit(m)} data-testid={`menu-edit-${m.id}`} className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => onDelete(m.id)} data-testid={`menu-delete-${m.id}`} className="h-8 w-8 rounded-lg hover:bg-destructive/10 flex items-center justify-center text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`menu-customers-${m.id}`}>
            {m.customers.length === 0 && <span className="text-xs text-muted-foreground">Müşteri atanmadı</span>}
            {m.customers.map((c) => <span key={c.id} className="rounded-full bg-muted px-2.5 py-0.5 text-xs">{c.short_name || c.name}</span>)}
          </div>
        </Card>
      ))}
    </div>
  </div>
);
